import React from 'react';
import SectionHeader from './SectionHeader';
import NotesInput from './NotesInput';
import LifeEventCheckbox from './LifeEventCheckbox';

/**
 * Section 4 — Financial &amp; Planning updates.
 *
 * Investment, account, insurance, and estate-level changes since the
 * last review.
 */
export default function StepFinancialPlanning({ data, onChange }) {
  const value = data || {};

  function update(field, val) {
    const next = { ...value, [field]: val };
    if (field !== 'none_of_the_above' && val) {
      next.none_of_the_above = false;
    }
    onChange(next);
  }

  function setNone(checked) {
    if (checked) {
      onChange({ none_of_the_above: true, notes: value.notes });
    } else {
      onChange({ ...value, none_of_the_above: false });
    }
  }

  const items = [
    {
      key: 'new_or_consolidated_accounts',
      label: 'Added, consolidated, or transferred accounts (bank, credit card, investment, etc.)',
      detailLabel: 'Provide a brief description and we\'ll request the statement on the final step.',
      detailKey: 'new_accounts_details',
    },
    {
      key: 'new_loan',
      label: 'Took out a new loan (mortgage, auto loan, HELOC, personal loan, etc.)',
      detailLabel: 'Enter a brief description of type(s) — we\'ll request the loan statement / closing docs on the final step. Full details (lender, amount, rate) can be entered on the Debts & Loans tab.',
      detailKey: 'new_loan_details',
    },
    {
      key: 'changed_investment_approach',
      label: 'Changed investment approach or preferences',
      detailLabel: 'Briefly describe the change',
      detailKey: 'changed_investment_approach_details',
    },
    {
      key: 'interested_in_strategies',
      label: 'Interested in strategies (Roth conversions, charitable giving, etc.)',
      detailLabel: 'Which strategies are you interested in?',
      detailKey: 'interested_in_strategies_details',
    },
    {
      key: 'large_purchase_or_sale',
      label: 'Made a large purchase or sale (home, second home, major asset)',
      detailLabel: 'Details / closing statement',
      detailKey: 'large_purchase_or_sale_details',
    },
    {
      key: 'insurance_changes',
      label: 'Purchased or updated insurance (life, disability, LTC, etc.)',
      detailLabel: 'Which policies and what changed?',
      detailKey: 'insurance_changes_details',
    },
    {
      key: 'estate_changes',
      label: 'Updated estate documents or beneficiaries',
      detailLabel: 'Briefly describe what changed',
      detailKey: 'estate_changes_details',
    },
    {
      key: 'inheritance_or_gift',
      label: 'Received an inheritance or large gift',
      detailLabel: 'Type, amount, and any details',
      detailKey: 'inheritance_or_gift_details',
    },
  ];

  return (
    <div>
      <SectionHeader
        number={4}
        title="Situation Updates — Financial &amp; Planning"
        tip="Check anything that applies. You'll have a chance on the
          final step to upload supporting documents."
        showAnnualNote={false}
      />

      {items.map(({ key, label, detailLabel, detailKey }) => (
        <LifeEventCheckbox
          key={key}
          id={key}
          label={label}
          checked={value[key]}
          onChange={(c) => update(key, c)}
        >
          <label className="form-label">{detailLabel}</label>
          <textarea
            className="form-control"
            rows={2}
            value={value[detailKey] || ''}
            onChange={(e) => update(detailKey, e.target.value)}
          />
        </LifeEventCheckbox>
      ))}

      <div className="form-check mt-3">
        <input
          className="form-check-input"
          type="checkbox"
          id="financial_none"
          checked={!!value.none_of_the_above}
          onChange={(e) => setNone(e.target.checked)}
        />
        <label className="form-check-label fw-semibold" htmlFor="financial_none">
          None of the above has changed since we last met.
        </label>
      </div>

      <NotesInput
        value={value.notes}
        onChange={(v) => update('notes', v)}
      />
    </div>
  );
}
